import type { Block, Doc, Format, Inline, List } from "./ast";
import { parse } from "./parse";

export type PlainOptions = {
  /** Writes a link's address after its words, as "runbook (https://...)". Useful for search. */
  withUrls?: boolean;
};

function inlineText(nodes: readonly Inline[], opts: PlainOptions): string {
  let out = "";
  for (const n of nodes) {
    switch (n.type) {
      case "text":
      case "code":
        out += n.text;
        break;
      case "bold":
      case "italic":
      case "strike":
        out += inlineText(n.children, opts);
        break;
      case "link": {
        const words = inlineText(n.children, opts);
        out += opts.withUrls && !n.auto && words !== n.href ? `${words} (${n.href})` : words;
        break;
      }
      case "mention":
        out += `@${n.name}`;
        break;
      case "channel":
        out += `#${n.name}`;
        break;
    }
  }
  return out;
}

function listText(list: List, depth: number, opts: PlainOptions): string[] {
  const lines: string[] = [];
  const pad = "  ".repeat(depth);
  list.items.forEach((item, i) => {
    const marker = list.ordered ? `${list.start + i}.` : "•";
    lines.push(`${pad}${marker} ${inlineText(item.children, opts)}`);
    for (const sub of item.lists) lines.push(...listText(sub, depth + 1, opts));
  });
  return lines;
}

function blockText(b: Block, opts: PlainOptions): string {
  switch (b.type) {
    case "paragraph":
    case "quote":
      return inlineText(b.children, opts);
    case "code_block":
      return b.text;
    case "list":
      return listText(b, 0, opts).join("\n");
  }
}

/** A parsed body as plain text. */
export function docToPlainText(doc: Doc, opts: PlainOptions = {}): string {
  return doc.blocks.map((b) => blockText(b, opts)).join("\n");
}

/**
 * A message body without its formatting marks, for chat list previews, search, notifications,
 * reply quotes and Queravel. A plain body comes back unchanged.
 */
export function toPlainText(body: string, format?: Format, opts: PlainOptions = {}): string {
  if (format !== "md") return body;
  return docToPlainText(parse(body, { format }), opts);
}
